import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, Medal, Flame, BookOpen, Star, Crown,
  TrendingUp, ArrowLeft, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const RANK_STYLES = {
  1: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: Crown },
  2: { bg: 'bg-zinc-400/10', border: 'border-zinc-400/30', text: 'text-zinc-300', icon: Medal },
  3: { bg: 'bg-orange-600/10', border: 'border-orange-600/30', text: 'text-orange-400', icon: Medal },
};

export default function PublicLeaderboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const currentUserId = localStorage.getItem('userId');
  const [period, setPeriod] = useState('all');
  const [leaderboard, setLeaderboard] = useState([]);
  const [topReaders, setTopReaders] = useState([]);
  const [topStreaks, setTopStreaks] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token, period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lb, readers, streaks] = await Promise.all([
        axios.get(`${API}/leaderboard/public?period=${period}&limit=10`, { headers }),
        axios.get(`${API}/leaderboard/public/top-readers`, { headers }),
        axios.get(`${API}/leaderboard/public/top-streaks`, { headers }),
      ]);
      setLeaderboard(lb.data.leaderboard || []);
      setTopReaders(readers.data || []);
      setTopStreaks(streaks.data || []);
    } catch (error) {
      console.error('[PublicLeaderboard] failed to load:', error?.message || error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="public-leaderboard" className="min-h-screen bg-black text-white pt-20">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Trophy className="w-5 h-5 text-amber-500" />
            <h1 className="text-lg font-bold">Reader Leaderboard</h1>
          </div>
          <div className="flex gap-1">
            {['all', 'month', 'week'].map(p => (
              <button
                key={p}
                data-testid={`period-${p}`}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  period === p ? 'bg-amber-500 text-black' : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                }`}
              >
                {p === 'all' ? 'All Time' : p === 'month' ? 'This Month' : 'This Week'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            {[1, 0, 2].map(displayIdx => {
              // displayIdx maps visual position: 0=left(#2), 1=center(#1), 2=right(#3)
              const dataIdx = displayIdx === 1 ? 0 : displayIdx === 0 ? 1 : 2;
              const entry = leaderboard[dataIdx];
              if (!entry) return null;
              const actualRank = dataIdx + 1;
              const style = RANK_STYLES[actualRank];
              const Icon = style.icon;
              const isMe = entry.user_id === currentUserId;

              return (
                <div
                  key={entry.user_id}
                  data-testid={`podium-${actualRank}`}
                  className={`${style.bg} border ${style.border} rounded-xl p-4 text-center ${
                    actualRank === 1 ? 'row-start-1 -mt-2' : ''
                  } ${isMe ? 'ring-2 ring-amber-500/50' : ''}`}
                >
                  <Icon className={`w-6 h-6 mx-auto mb-2 ${style.text}`} />
                  <div className={`text-2xl font-black ${style.text}`}>#{actualRank}</div>
                  <p className="text-sm font-bold text-white mt-1 truncate">{entry.name}</p>
                  <p className="text-xs text-zinc-400 mt-1">{entry.score} pts</p>
                  <div className="flex justify-center gap-3 mt-2 text-[10px] text-zinc-500">
                    <span><BookOpen className="w-3 h-3 inline mr-0.5" />{entry.books_completed}</span>
                    <span><Flame className="w-3 h-3 inline mr-0.5" />{entry.current_streak}d</span>
                  </div>
                  {isMe && <span className="text-[10px] text-amber-400 font-medium mt-1 block">You</span>}
                </div>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Leaderboard */}
          <div className="lg:col-span-2 bg-zinc-900/80 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" /> Rankings
              </h2>
            </div>

            {loading ? (
              <div className="p-8 text-center text-zinc-500">Loading...</div>
            ) : leaderboard.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">No data yet. Start reading!</div>
            ) : (
              <div className="divide-y divide-zinc-800/50">
                {leaderboard.map((entry) => {
                  const isMe = entry.user_id === currentUserId;
                  return (
                    <div
                      key={entry.user_id}
                      data-testid={`leaderboard-row-${entry.rank}`}
                      className={`px-4 py-3 flex items-center gap-3 ${
                        isMe ? 'bg-amber-500/5' : 'hover:bg-zinc-800/30'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        entry.rank <= 3
                          ? `${RANK_STYLES[entry.rank]?.bg} ${RANK_STYLES[entry.rank]?.text}`
                          : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        {entry.rank}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {entry.name} {isMe && <span className="text-amber-400 text-xs">(You)</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-zinc-400">
                        <span className="hidden sm:flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> {entry.books_completed}
                        </span>
                        <span className="hidden sm:flex items-center gap-1">
                          <Flame className="w-3 h-3 text-orange-400" /> {entry.current_streak}d
                        </span>
                        <span className="font-bold text-white min-w-[50px] text-right">{entry.score} pts</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Top Readers */}
            <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-800">
                <h3 className="text-xs font-bold text-white flex items-center gap-2">
                  <BookOpen className="w-3.5 h-3.5 text-blue-400" /> Top Readers
                </h3>
              </div>
              <div className="divide-y divide-zinc-800/50">
                {topReaders.map((r, i) => (
                  <div key={r.user_id} className="px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 w-4">{i + 1}.</span>
                      <span className="text-xs text-white">{r.name}</span>
                    </div>
                    <span className="text-xs font-bold text-blue-400">{r.books_completed} books</span>
                  </div>
                ))}
                {topReaders.length === 0 && (
                  <div className="p-4 text-xs text-zinc-500 text-center">No data yet</div>
                )}
              </div>
            </div>

            {/* Top Streaks */}
            <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-800">
                <h3 className="text-xs font-bold text-white flex items-center gap-2">
                  <Flame className="w-3.5 h-3.5 text-orange-400" /> Top Streaks
                </h3>
              </div>
              <div className="divide-y divide-zinc-800/50">
                {topStreaks.map((s, i) => (
                  <div key={s.user_id} className="px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 w-4">{i + 1}.</span>
                      <span className="text-xs text-white">{s.name}</span>
                    </div>
                    <span className="text-xs font-bold text-orange-400">{s.longest_streak}d best</span>
                  </div>
                ))}
                {topStreaks.length === 0 && (
                  <div className="p-4 text-xs text-zinc-500 text-center">No data yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
